// src/hooks/useDashboardViewSync.ts
import { useCallback, useEffect, useRef } from 'react';
import { dashboardApi, toApiGridSize } from '@/services/dashboardApi';

// Delay so dragging the slider sends a single PUT
const RESIZE_DEBOUNCE_MS = 400;

type PersistDone = (apiGridSize: number) => void;

// Persists dashboard view type and grid size via PUT dashboard/updatedashboardview
export function useDashboardViewSync()
{
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() =>
  {
    return () =>
    {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const persistDashboardView = useCallback(
    (dashboardId: number, viewTypeId: number, columnWidthPx: number, onDone?: PersistDone) =>
    {
      const apiGridSize = toApiGridSize(columnWidthPx);

      dashboardApi
        .updateDashboardView(dashboardId, apiGridSize, viewTypeId)
        .then(() =>
        {
          onDone?.(apiGridSize);
        })
        .catch(err =>
        {
          console.error('Failed to update dashboard view:', err);
        });
    },
    [],
  );

  const persistDashboardViewDebounced = useCallback(
    (dashboardId: number, viewTypeId: number, columnWidthPx: number, onDone?: PersistDone) =>
    {
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() =>
      {
        timerRef.current = null;
        persistDashboardView(dashboardId, viewTypeId, columnWidthPx, onDone);
      }, RESIZE_DEBOUNCE_MS);
    },
    [persistDashboardView],
  );

  return { persistDashboardView, persistDashboardViewDebounced };
}
